import Layout from "../../hocs/Layout";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { Button, Col, Form, Row, Container, Card } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import { perfilSchema } from "../../utils/validationSchemas";
import { patch_user } from "../../redux/actions/auth";
import { getOfficesERP } from "../../services/usuarios";
import { TextInput } from "../../components/inputs/Inputs";

const MiPerfil = ({ user, patch_user }) => {
  const navigate = useNavigate();
  const defaultValues = {
    email: "",
    first_name: "",
    last_name: "",
    document_id: "",
    phone: "",
    responsible: "",
    prescriptor: "",
    bank_account: "",
    erp_user_id: "",
    delegacion: ""
  };

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty, errors, dirtyFields },
    setValue
  } = useForm({
    mode: "onBlur",
    defaultValues,
    resolver: yupResolver(perfilSchema),
  });

  const [editMode, setEditMode] = useState(false);

  // Oficinas
  const [officesOptions, setOfficesOptions] = useState([]);
  const [loadingOffices, setLoadingOffices] = useState(false);

  // Cargar oficinas
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        setLoadingOffices(true);
        const offices = await getOfficesERP(); // [{id, name}, ...]
        setOfficesOptions(offices.map(o => ({ value: String(o.id), label: o.name })));
      } catch (e) {
        console.error("Error cargando oficinas:", e);
      } finally {
        setLoadingOffices(false);
      }
    };
    fetchOffices();
  }, []);

  // Reset con datos de usuario
  const resetData = useCallback(() => {
    const delegacionId =
      user?.delegacion?.id ??
      user?.delegacion_id ??
      (typeof user?.delegacion === "number" || typeof user?.delegacion === "string" ? user.delegacion : "");

    reset({
      email: user?.email ?? "",
      first_name: user?.first_name ?? "",
      last_name: user?.last_name ?? "",
      document_id: user?.document_id ?? "",
      phone: user?.phone ?? "",
      responsible: user?.responsible ?? "",
      prescriptor: user?.prescriptor ?? "",
      bank_account: user?.bank_account ?? "",
      erp_user_id: user?.erp_user_id ?? "",
      delegacion: delegacionId ? String(delegacionId) : ""
    });
  }, [user, reset]);

  useEffect(() => {
    resetData();
  }, [resetData]);

  const cancelEditar = () => {
    setEditMode(false);
    resetData();
  };

  const onSubmit = async () => {
    const form = {};
    Object.entries(getValues())
      .filter(([key]) => !!dirtyFields[key])
      .forEach(([k, v]) => (form[k] = v));

    // Cast delegación a número si viene del select
    if (form.delegacion !== undefined && form.delegacion !== "") {
      form.delegacion = parseInt(form.delegacion, 10);
    }

    try {
      await patch_user(form);
      setEditMode(false);
    } catch (error) {
      console.error(error);
      resetData();
    }
  };

  return (
    <Layout>
      <div className="min-vh-100 d-flex align-items-center bg-light py-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} md={10} xs={12}>
              <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                <Card.Header className="bg-white border-bottom-0 pt-4 pb-0 text-center">
                  <h2 className="text-primary fw-bold mb-0">Mi Perfil</h2>
                </Card.Header>
                <Card.Body className="p-4 p-md-5">
                  <Form onSubmit={handleSubmit(onSubmit)}>
                    {/* Documento y Delegación */}
                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Controller
                          name="document_id"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="document_id"
                              label="Documento"
                              readOnly={!editMode}
                              error={errors.document_id?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="delegacion"
                          control={control}
                          render={({ field }) => (
                            <div className="form-floating">
                              <Form.Select
                                {...field}
                                className="form-control rounded-3 shadow-sm border-light bg-white"
                                disabled={!editMode || loadingOffices}
                                isInvalid={!!errors.delegacion}
                                id="delegacionSelect"
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                  setValue("delegacion", e.target.value, { shouldDirty: true, shouldValidate: true });
                                }}
                              >
                                <option value="">
                                  {loadingOffices ? "Cargando..." : "Seleccione delegación"}
                                </option>
                                {officesOptions.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </Form.Select>
                              <label htmlFor="delegacionSelect">Delegación</label>
                              <Form.Control.Feedback type="invalid">
                                {errors.delegacion?.message}
                              </Form.Control.Feedback>
                            </div>
                          )}
                        />
                      </Col>
                    </Row>

                    {/* Nombre y Apellido */}
                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Controller
                          name="first_name"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="first_name"
                              label="Nombre / Razón Social"
                              readOnly={!editMode}
                              error={errors.first_name?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="last_name"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="last_name"
                              label="Apellidos"
                              readOnly={!editMode}
                              error={errors.last_name?.message}
                            />
                          )}
                        />
                      </Col>
                    </Row>

                    {/* Email y Teléfono */}
                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Controller
                          name="email"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="email"
                              label="Correo electrónico"
                              readOnly
                              disabled // Email usually not editable or handled separately
                              error={errors.email?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="phone"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="phone"
                              label="Teléfono"
                              readOnly={!editMode}
                              error={errors.phone?.message}
                            />
                          )}
                        />
                      </Col>
                    </Row>

                    {/* Gestor y Prescriptor */}
                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Controller
                          name="responsible"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="responsible"
                              label="Gestor de cuenta"
                              readOnly
                              disabled
                              error={errors.responsible?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="prescriptor"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="prescriptor"
                              label="Prescriptor"
                              readOnly
                              disabled
                              error={errors.prescriptor?.message}
                            />
                          )}
                        />
                      </Col>
                    </Row>

                    {/* ERP ID y IBAN */}
                    <Row className="g-3 mb-4">
                      <Col md={6}>
                        <Controller
                          name="erp_user_id"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="erp_user_id"
                              label="ERP User ID"
                              readOnly
                              disabled
                              error={errors.erp_user_id?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col md={6}>
                        <Controller
                          name="bank_account"
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              field={field}
                              name="bank_account"
                              label="IBAN"
                              readOnly
                              disabled
                              error={errors.bank_account?.message}
                            />
                          )}
                        />
                      </Col>
                    </Row>

                    {/* Botonera */}
                    <div className="d-flex justify-content-center mt-4">
                      {editMode ? (
                        <div className="d-flex gap-3 w-100 justify-content-center">
                          <Button
                            variant="primary"
                            className="rounded-pill px-5 py-2 fw-bold shadow-sm"
                            type="submit"
                            disabled={!isDirty}
                          >
                            Guardar
                          </Button>
                          <Button
                            variant="outline-secondary"
                            className="rounded-pill px-5 py-2 fw-bold shadow-sm"
                            onClick={cancelEditar}
                          >
                            Cancelar
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="primary"
                          className="rounded-pill px-5 py-2 fw-bold shadow-sm"
                          onClick={(e) => {
                            e.preventDefault();
                            setEditMode(true);
                          }}
                        >
                          Editar Perfil
                        </Button>
                      )}
                    </div>
                  </Form>
                </Card.Body>
              </Card>

              {/* Admin Actions Card - Only for Superadmins */}
              {user?.is_superadmin && (
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden mt-4">
                  <Card.Header className="bg-white border-bottom-0 pt-4 pb-0 text-center">
                    <h4 className="text-secondary fw-bold mb-0">Gestión de Usuarios</h4>
                  </Card.Header>
                  <Card.Body className="p-4 text-center">
                    <div className="d-flex justify-content-center gap-3 flex-wrap">
                      <Button
                        variant="outline-primary"
                        className="rounded-pill px-4 py-2 fw-bold shadow-sm"
                        onClick={() => navigate("/signup")}
                      >
                        Registrar Nuevo Usuario
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  user: state.Auth.user,
});

export default connect(mapStateToProps, { patch_user })(MiPerfil);
